/**
 * A friendly robot-accountant avatar, drawn as self-contained inline SVG (no
 * external assets, crisp at any size, theme-independent). Classic accountant
 * cues: green eyeshade visor, round glasses, a bow tie, and a coin-topped
 * antenna. Subtle idle animations (blink, antenna pulse) are opt-in via CSS
 * classes and can be disabled with `animated={false}`.
 */
export default function RobotAccountantAvatar({ size = 44, animated = true }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="hlbBotBody" x1="32" y1="12" x2="32" y2="48">
          <stop offset="0" stopColor="#f4f7fb" />
          <stop offset="1" stopColor="#cbd5e3" />
        </linearGradient>
        <linearGradient id="hlbBotVisor" x1="32" y1="16" x2="32" y2="28">
          <stop offset="0" stopColor="#37b24d" />
          <stop offset="1" stopColor="#2b8a3e" />
        </linearGradient>
        <radialGradient id="hlbBotCoin" cx="0.5" cy="0.4" r="0.6">
          <stop offset="0" stopColor="#ffe08a" />
          <stop offset="1" stopColor="#f0a500" />
        </radialGradient>
      </defs>

      {/* Antenna */}
      <line x1="32" y1="14" x2="32" y2="8" stroke="#9aa7b5" strokeWidth="2" strokeLinecap="round" />
      <g className={animated ? 'hlb-bot-antenna' : undefined}>
        <circle cx="32" cy="6" r="4.4" fill="#f0a500" opacity="0.25" />
        <circle cx="32" cy="6" r="3" fill="url(#hlbBotCoin)" stroke="#d9930b" strokeWidth="0.6" />
      </g>

      {/* Side bolts / ears */}
      <rect x="8.5" y="27" width="5" height="11" rx="2.2" fill="#b7c2d0" />
      <rect x="50.5" y="27" width="5" height="11" rx="2.2" fill="#b7c2d0" />

      {/* Head */}
      <rect x="13" y="13" width="38" height="35" rx="11" fill="url(#hlbBotBody)" stroke="#aab6c5" strokeWidth="1" />
      <ellipse cx="26" cy="21" rx="9" ry="3.4" fill="#ffffff" opacity="0.5" />

      {/* Green accountant eyeshade visor */}
      <path
        d="M13 25 Q13 16.5 24 16.5 L40 16.5 Q51 16.5 51 25 L51 26.4 Q51 27.6 49.6 27.6 L14.4 27.6 Q13 27.6 13 26.4 Z"
        fill="url(#hlbBotVisor)"
      />
      <path d="M14 27.6 L50 27.6" stroke="#1f6b2b" strokeWidth="0.8" opacity="0.5" />

      {/* Glasses bridge */}
      <line x1="29" y1="35" x2="35" y2="35" stroke="#5a6b7b" strokeWidth="1.6" strokeLinecap="round" />

      {/* Eyes / glasses */}
      <g
        className={animated ? 'hlb-bot-eyes' : undefined}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <circle cx="24.5" cy="35" r="6.2" fill="#141b22" stroke="#5a6b7b" strokeWidth="1.6" />
        <circle cx="39.5" cy="35" r="6.2" fill="#141b22" stroke="#5a6b7b" strokeWidth="1.6" />
        <circle cx="24.5" cy="35" r="2.7" fill="#4dd0e1" />
        <circle cx="39.5" cy="35" r="2.7" fill="#4dd0e1" />
        <circle cx="25.6" cy="33.9" r="0.9" fill="#ffffff" />
        <circle cx="40.6" cy="33.9" r="0.9" fill="#ffffff" />
      </g>

      {/* Friendly smile */}
      <path d="M27 43 Q32 46.5 37 43" stroke="#5a6b7b" strokeWidth="2" fill="none" strokeLinecap="round" />

      {/* Collar + bow tie */}
      <rect x="28" y="47" width="8" height="3" fill="#cbd5e3" />
      <path d="M32 50 L25.5 47 L25.5 53 Z" fill="#c0392b" />
      <path d="M32 50 L38.5 47 L38.5 53 Z" fill="#c0392b" />
      <circle cx="32" cy="50" r="1.8" fill="#96271b" />
    </svg>
  );
}
